'use client';

import { Bleed, BlockStack, Box, Select, Tabs, Card, InlineStack, Text } from '@shopify/polaris';
import { startTransition, useCallback, useState, memo, useMemo } from 'react';
import { StudioVideos } from '@/features/studio-videos/StudioVideos';
import { VideoAssets } from '@/features/media-assets/components/VideoAssets';
import { ImageAssets } from '@/features/media-assets/components/ImageAssets';
import AllCreationsList from '@/features/my-creations-library/components/AllCreationsList';
import { LibraryNotificationsContainer } from '@/features/notifications/LibraryNotificationsContainer';
import { LibraryNotificationsStoreProvider } from '@/features/notifications/stores/libraryNotificationsStore';
import { DraftStudioVideos } from '@/features/studio-videos/DraftStudioVideos';
import { Modal, TitleBar } from '@shopify/app-bridge-react';
import PublishWebsite from '@/features/publish/PublishWebsite';
import { useSearchParams } from 'next/navigation';
import { TemplateFeatures, TemplateName } from '@/features/studio-video-templates/types/template';
import { TEMPLATES } from '@/features/studio-video-templates/constants/template';
import { LibraryViews } from '@/features/my-creations-library/contants';
import usePageNavigation from '@/hooks/usePageNavigation';
import { Page } from '@/components/Page';
import { useStudioVideosPrefetch } from '@/features/studio-videos/hooks/useStudioVideos';
import * as MediaAssetAPI from '@/api/media-asset';
import { useMediaAssetsPrefetch } from '@/hooks/useMediaAssetsLoader';
import { Product } from '@/types/product';
import ProductFilter from '@/features/products/filters/ProductFilter';
import { PersonalAvatarsList } from '@/features/personal-avatars/PersonalAvatarsList';
interface PanelProps {
  creationTemplateFilter?: TemplateName | 'all';
  selectedFeatures?: Set<TemplateFeatures>;
  selectedProduct?: Product | null;
}

const tabs = [
  {
    id: LibraryViews.ALL,
    content: 'All',
    accessibilityLabel: 'All',
    panelID: LibraryViews.ALL,
    PanelContent: AllCreationsList as React.ComponentType<PanelProps>,
  },
  {
    id: LibraryViews.STUDIO_VIDEOS,
    content: 'Videos',
    accessibilityLabel: 'Videos',
    panelID: LibraryViews.STUDIO_VIDEOS,
    PanelContent: StudioVideos as React.ComponentType<PanelProps>,
  },
  {
    id: LibraryViews.AI_VIDEOS,
    content: 'Animations',
    accessibilityLabel: 'Animations',
    panelID: LibraryViews.AI_VIDEOS,
    PanelContent: VideoAssets as React.ComponentType<PanelProps>,
  },
  {
    id: LibraryViews.AI_IMAGES,
    content: 'Images',
    accessibilityLabel: 'Images',
    panelID: LibraryViews.AI_IMAGES,
    PanelContent: ImageAssets as React.ComponentType<PanelProps>,
  },
  {
    id: LibraryViews.INFLUENCERS,
    content: 'Influencers',
    accessibilityLabel: 'Influencers',
    panelID: LibraryViews.INFLUENCERS,
    PanelContent: PersonalAvatarsList as React.ComponentType<PanelProps>,
  },
  {
    id: LibraryViews.DRAFT_STUDIO_VIDEOS,
    content: 'Draft',
    accessibilityLabel: 'Draft',
    panelID: LibraryViews.DRAFT_STUDIO_VIDEOS,
    PanelContent: DraftStudioVideos as React.ComponentType<PanelProps>,
  },
];

const creationTemplateFilterOptions = [
  {
    label: 'All',
    value: 'all',
  },
  ...TEMPLATES.map((template) => ({
    label: template.title,
    value: template.id,
  })),
];

const studioVideoViewIndex = tabs.findIndex((tab) => tab.id === LibraryViews.STUDIO_VIDEOS);

const LibraryRoute = () => {
  const searchParams = useSearchParams();
  const viewParam = searchParams.get('view');
  const creationTemplateFilterParam = searchParams.get('creationTemplateFilter');
  const [view, setView] = useState<number>(() => {
    const index = tabs.findIndex((tab) => tab.id === viewParam);
    return index !== -1 ? index : 0;
  });
  const [selectedFeatures, setSelectedFeatures] = useState<Set<TemplateFeatures>>(new Set());
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const [creationTemplateFilter, setCreationTemplateFilter] = useState<TemplateName | 'all'>(
    (creationTemplateFilterParam as TemplateName) || 'all'
  );
  const PanelContent = tabs[view].PanelContent;
  const isInfluencerView = view === tabs.findIndex((tab) => tab.id === LibraryViews.INFLUENCERS);
  const handleSetView = (view: number) => {
    startTransition(() => {
      setView(view);
      const url = new URL(window.location.href);
      url.searchParams.set('view', tabs[view].id);
      window.history.replaceState({}, '', url);
    });
  };

  const handleProductChange = useCallback((product: Product | null) => {
    setSelectedProduct(product);
  }, []);

  const handleSetCreationTemplateFilter = (template: TemplateName) => {
    setCreationTemplateFilter(template);
    const url = new URL(window.location.href);
    url.searchParams.set('creationTemplateFilter', template);
    window.history.replaceState({}, '', url);
  };

  const toggleFeature = useCallback((feature: TemplateFeatures) => {
    setSelectedFeatures((prev) => {
      const next = new Set(prev);

      if (next.has(feature)) {
        next.delete(feature);
      } else {
        next.add(feature);
      }
      return next;
    });
  }, []);

  const { navigateBack, previousPageTitle } = usePageNavigation();
  useStudioVideosPrefetch();
  const mediaAssetQueryKeys = useMemo(
    () => ({
      videoAssets: ['video-assets'],
      imageAssets: ['image-assets'],
    }),
    []
  );
  useMediaAssetsPrefetch({ queryKey: mediaAssetQueryKeys.videoAssets, loaderFn: MediaAssetAPI.getAiVideos });
  useMediaAssetsPrefetch({ queryKey: mediaAssetQueryKeys.imageAssets, loaderFn: MediaAssetAPI.getAiImages });

  return (
    <LibraryNotificationsStoreProvider>
      <Page backAction={{ content: previousPageTitle, onAction: navigateBack }} title="Library">
        <LibraryNotificationsContainer />
        <Bleed marginInline="300">
          <div className="flex items-center justify-between">
            <Tabs tabs={tabs} selected={view} onSelect={handleSetView} />
          </div>
          <Box paddingInline="300" paddingBlockStart="400" paddingBlockEnd="400">
            <BlockStack gap="400">
              {view === studioVideoViewIndex ? (
                <Card>
                  <InlineStack gap="200" blockAlign="start">
                    <Box width="280px">
                      <BlockStack gap="200">
                        <Text as="h3" variant="bodyMd" fontWeight="medium" tone="subdued">
                          Creation template
                        </Text>
                        <Select
                          label="Creation template"
                          labelHidden
                          options={creationTemplateFilterOptions}
                          value={creationTemplateFilter}
                          onChange={handleSetCreationTemplateFilter}
                        />
                      </BlockStack>
                    </Box>

                    <div className="mx-4 w-px self-stretch bg-border" />

                    <ProductFilter selectedProduct={selectedProduct} onChange={handleProductChange} bold />

                    <div className="mx-4 w-px self-stretch bg-border" />
                  </InlineStack>
                </Card>
              ) : (
                !isInfluencerView && (
                  <Card>
                    <InlineStack gap="200" blockAlign="start">
                      <ProductFilter selectedProduct={selectedProduct} onChange={handleProductChange} bold />
                    </InlineStack>
                  </Card>
                )
              )}
              <PanelContent
                creationTemplateFilter={creationTemplateFilter}
                selectedFeatures={selectedFeatures}
                selectedProduct={selectedProduct}
              />
            </BlockStack>
          </Box>
        </Bleed>
      </Page>
      <Modal id="publish-to-website" variant="large">
        <TitleBar title="Publish to website" />
        <Box padding="400">
          <PublishWebsite />
        </Box>
      </Modal>
    </LibraryNotificationsStoreProvider>
  );
};

export default memo(LibraryRoute);
